import { Link } from "react-router";
import { VariantProps, cva } from "class-variance-authority";
import { ButtonHTMLAttributes, AnchorHTMLAttributes, forwardRef, ReactNode } from "react";
import { cn } from "~/lib/utils";

const variants = cva(
    "focusable pointer-events-auto animate-fade cursor-pointer rounded-full text-center transition-all disabled:cursor-not-allowed",
    {
        variants: {
            buttonStyle: {
                // old styles above, new below.
                primary: [
                    "bg-blue-80",
                    "text-white",
                    "hover:bg-blue-90",
                    "hover:shadow-gray",
                    "disabled:bg-gray-70",
                ],
                secondary: [
                    "bg-white",
                    "text-blue-80",
                    "border",
                    "border-blue-80",
                    "hover:bg-blue-20",
                    "disabled:border-gray-70",
                    "disabled:text-gray-70",
                ],
                tertiary: ["bg-blue-20", "text-blue-80", "hover:bg-blue-30", "disabled:bg-gray-70"],
                "destructive-primary": [
                    "bg-error-90",
                    "text-white",
                    "hover:bg-error-100",
                    "disabled:bg-gray-30",
                    "disabled:text-gray-80",
                ],
                "destructive-secondary": [
                    "text-error-90",
                    "bg-white",
                    "border",
                    "border-error-90",
                    "hover:text-error-100",
                    "hover:bg-gray-20",
                    "disabled:border-gray-70",
                    "disabled:text-gray-70",
                ],
            },
            size: {
                sm: ["px-[20px]", "py-[4px]"],
                lg: ["px-[20px]", "py-[12px]"],
            },
        },
        defaultVariants: {
            buttonStyle: "primary",
            size: "sm",
        },
    },
);

export type Size = NonNullable<VariantProps<typeof variants>["size"]>;

interface ButtonProps
    extends ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof variants> {
    disabled?: boolean;
}

export interface ButtonIconProps
    extends ButtonHTMLAttributes<HTMLButtonElement>,
        Omit<VariantProps<typeof variants>, "size"> {
    icon: ReactNode | string;
    label?: string;
    disabled?: boolean;
}

interface ButtonLinkProps
    extends AnchorHTMLAttributes<HTMLAnchorElement>,
        VariantProps<typeof variants> {
    href: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({ buttonStyle, size, children, className, onClick, ...props }, ref) => {
        const classNames = cn(variants({ buttonStyle, size }), className);
        return (
            <button ref={ref} className={classNames} onClick={onClick} {...props}>
                {children}
            </button>
        );
    },
);

export function ButtonLink({
    buttonStyle,
    size,
    children,
    className,
    href,
    ...props
}: ButtonLinkProps) {
    const classNames = cn(variants({ buttonStyle, size }), className);
    return (
        <Link rel="nofollow" className={classNames} to={href} {...props}>
            {children}
        </Link>
    );
}
